import { forwardRef } from 'react'

/**
 * IconInput — Input with live Tabler icon preview + search link.
 * Usage: same as <Input> but shows the typed icon class live.
 *
 * <IconInput label="Icon" {...register('icon')} value={watch('icon')} />
 */
export const IconInput = forwardRef(function IconInput(
  { label, value, error, hint, ...props },
  ref
) {
  const hasIcon = value && value.startsWith('ti-')

  return (
    <div className="flex flex-col gap-1.5">
      {/* Label row */}
      <div className="flex items-center justify-between">
        {label && (
          <label className="text-sm font-medium text-gray-700">{label}</label>
        )}
        <a
          href="https://tabler.io/icons"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 hover:underline"
          tabIndex={-1}
        >
          <i className="ti ti-external-link text-[11px]" />
          Browse icons
        </a>
      </div>

      {/* Input + preview */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            ref={ref}
            value={value}
            placeholder="ti-building-store"
            className={[
              'h-10 w-full rounded-lg border bg-white px-3 pr-9 text-sm text-gray-900 placeholder:text-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500',
              error ? 'border-red-400' : 'border-gray-200',
            ].join(' ')}
            {...props}
          />
          {/* Live icon inside input */}
          {hasIcon && (
            <i className={`ti ${value} absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-base pointer-events-none`} />
          )}
        </div>

        {/* Preview badge */}
        <div className={[
          'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border text-lg transition-all',
          hasIcon ? 'border-indigo-100 bg-indigo-50 text-indigo-600' : 'border-gray-100 bg-gray-50 text-gray-300',
        ].join(' ')}>
          <i className={hasIcon ? `ti ${value}` : 'ti ti-photo'} />
        </div>
      </div>

      {/* Hint */}
      {hint && !error && (
        <p className="text-xs text-gray-400">{hint}</p>
      )}

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}

      {/* Quick examples */}
      {!value && (
        <div className="flex flex-wrap gap-1.5 mt-0.5">
          {['ti-tools-kitchen-2','ti-shopping-bag','ti-scissors','ti-building-store','ti-star','ti-map-pin','ti-phone'].map(ic => (
            <button
              key={ic}
              type="button"
              onClick={() => props.onChange?.({ target: { value: ic } })}
              className="flex items-center gap-1 rounded-md border border-gray-100 bg-gray-50 px-2 py-0.5 text-xs text-gray-500 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
              title={ic}
            >
              <i className={`ti ${ic} text-sm`} />
              <span className="hidden sm:inline">{ic}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
})
